import type { TFunction } from "i18next";
import type { PmActivity, PmPhase } from "@arc/contracts";
import { phaseLabel } from "./pm-labels.js";

/**
 * One history row as a sentence ("hat ihn von Offen nach ISV verschoben") — for a task's own history and the MS's
 * Verlauf. The row's `before`/`after` snapshots carry what the sentence needs (column titles at the time, file names,
 * versions), so a later rename never rewrites history. A combination this does not know stays readable
 * ("hat etwas geändert") instead of showing a key.
 */
export function activityText(t: TFunction, a: PmActivity, ctx: { stepNumber?: (stepId: number) => number | null } = {}): string {
  const before = (a.before ?? {}) as Record<string, unknown>;
  const after = (a.after ?? {}) as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === "string" ? v : "");
  const num = (v: unknown) => (typeof v === "number" ? v : null);
  switch (a.objectType) {
    case "task":
      if (a.action === "moved") {
        const within = num(before.columnId) !== null && before.columnId === after.columnId;
        return within ? t("pmBoard.activity.task.reordered", { column: str(after.column) }) : t("pmBoard.activity.task.moved", { from: str(before.column), to: str(after.column) });
      }
      if (a.action === "tags_changed") {
        const tags = Array.isArray(after.tags) ? after.tags.filter((x): x is string => typeof x === "string") : [];
        return tags.length > 0 ? t("pmBoard.activity.task.tags_changed", { tags: tags.join(", ") }) : t("pmBoard.activity.task.tags_cleared");
      }
      if (a.action === "created" || a.action === "edited" || a.action === "flagged" || a.action === "unflagged" || a.action === "deleted") {
        return t(`pmBoard.activity.task.${a.action}`);
      }
      break;
    case "task_annotation":
      if (a.action === "created" || a.action === "edited" || a.action === "deleted") return t(`pmBoard.activity.task_annotation.${a.action}`);
      break;
    case "task_media":
      if (a.action === "created" || a.action === "deleted") {
        return t(`pmBoard.activity.task_media.${a.action}`, { name: str(after.fileName) || str(before.fileName) });
      }
      break;
    case "step_annotation":
      if (a.action === "created" || a.action === "edited" || a.action === "deleted") {
        const stepId = num(after.stepId) ?? num(before.stepId);
        const n = stepId !== null ? ctx.stepNumber?.(stepId) : null;
        return t(`pmBoard.activity.step_annotation.${a.action}`, { step: n ?? "?" });
      }
      break;
    case "variant_annotation":
      if (a.action === "created" || a.action === "edited" || a.action === "deleted") {
        return t(`pmBoard.activity.variant_annotation.${a.action}`, { variant: str(after.variant) || str(before.variant) });
      }
      break;
    case "micro_scenario":
      if (a.action === "phase_changed") {
        const phase = str(after.phase) as PmPhase;
        return phase ? t("pmBoard.activity.micro_scenario.phase_changed", { phase: phaseLabel(t, { phase, prototypeNumber: num(after.prototypeNumber) }) }) : t("pmBoard.activity.other");
      }
      if (a.action === "created" || a.action === "edited" || a.action === "training_url_changed") return t(`pmBoard.activity.micro_scenario.${a.action}`);
      break;
    case "drehbuch":
      if (a.action === "imported") return t("pmBoard.activity.drehbuch.imported", { version: num(after.version) ?? "?" });
      break;
    default:
      break;
  }
  return t("pmBoard.activity.other");
}

/** The task a row is about (a task row itself, or a note / file on one) — for "an „…“" in the MS history. */
export function activityTaskId(a: PmActivity): number | null {
  if (a.objectType === "task") return a.objectId;
  if (a.objectType === "task_annotation" || a.objectType === "task_media") {
    const v = ((a.after ?? a.before ?? {}) as Record<string, unknown>).taskId;
    return typeof v === "number" ? v : null;
  }
  return null;
}
